import { z } from 'zod';

export const BillingTierSchema = z.enum([
  'specialized_frontier',
  'heavyweight_reasoning',
  'production_utility',
]);

export const BillingTierCapacitySchema = z.object({
  tier: BillingTierSchema,
  displayName: z.string(),
  consumedUsd: z.number(),
  budgetUsd: z.number().nullable(),
  percentUsed: z.number().nullable(),
});

export const BillingAiCapacitySchema = z.object({
  enabled: z.boolean(),
  consumedUsd: z.number(),
  budgetUsd: z.number().nullable(),
  percentUsed: z.number().nullable(),
  tiers: z.array(BillingTierCapacitySchema),
});

export const BillingAiCapacityTrendGranularitySchema = z.enum([
  'day',
  'week',
  'month',
]);

export const BillingAiCapacityTrendTierSchema = z.object({
  tier: BillingTierSchema,
  displayName: z.string(),
  consumedUsd: z.number(),
});

export const BillingAiCapacityTrendPointSchema = z.object({
  date: z.string(),
  totalUsd: z.number(),
  tiers: z.array(BillingAiCapacityTrendTierSchema),
});

export const BillingAiCapacityTrendSchema = z.object({
  enabled: z.boolean(),
  granularity: BillingAiCapacityTrendGranularitySchema,
  points: z.array(BillingAiCapacityTrendPointSchema),
});

export const BillingMarginRowSchema = z.object({
  modelName: z.string(),
  tier: BillingTierSchema,
  inputTokens: z.number(),
  outputTokens: z.number(),
  totalTokens: z.number(),
  internalCostUsd: z.number(),
  internalCostPer1MUsd: z.number(),
  customerPriceUsd: z.number(),
  customerPricePer1MUsd: z.number(),
  marginPercent: z.number(),
  markup: z.number(),
  estimatedTokensThroughTerm: z.number(),
  projectedInternalCostUsd: z.number(),
  expectedMarginPercent: z.number(),
});

export const BillingMarginSummarySchema = z.object({
  internalCostUsd: z.number(),
  customerPriceUsd: z.number(),
  currentMarginPercent: z.number(),
  projectedInternalCostUsd: z.number(),
  budgetUsd: z.number().nullable(),
  expectedMarginPercent: z.number().nullable(),
});

export const BillingMarginSchema = z.object({
  rows: z.array(BillingMarginRowSchema),
  summary: BillingMarginSummarySchema,
  termStart: z.string().nullable(),
  termEnd: z.string().nullable(),
});

export const BillingBaselineMetricSchema = z.object({
  completed: z.number(),
  total: z.number(),
  budget: z.number().nullable(),
  percentOfBudget: z.number().nullable(),
});

export const BillingBaselineSchema = z.object({
  applications: BillingBaselineMetricSchema,
  networkAssets: BillingBaselineMetricSchema,
});

export const BillingCapacityForecastSchema = z.object({
  termEnd: z.string().nullable(),
  remainingDays: z.number().nonnegative(),
  consumedUsd: z.number(),
  incrementalScansUsd: z.number(),
  networkScansUsd: z.number(),
  otherUsageUsd: z.number(),
  onboardingUsd: z.number(),
  projectedTotalUsd: z.number(),
  budgetUsd: z.number().nullable(),
  projectedPercentOfBudget: z.number().nullable(),
  projectedExhaustionDate: z.string().nullable(),
  drivers: z.object({
    applicationDailyCostUsd: z.number(),
    applicationOnboardingCostUsd: z.number(),
    onboardedApplications: z.number().int().nonnegative(),
    pendingApplications: z.number().int().nonnegative(),
    networkDailyCostPerAssetUsd: z.number(),
    onboardedNetworkAssets: z.number().int().nonnegative(),
    pendingNetworkAssets: z.number().int().nonnegative(),
    otherDailyCostUsd: z.number(),
  }),
});

export const BillingOverviewResponseSchema = z.object({
  capacity: BillingAiCapacitySchema,
  baseline: BillingBaselineSchema,
  margin: BillingMarginSchema,
});

export const BillingPortfolioTenantSchema = z.object({
  tenantId: z.string(),
  licenseName: z.string().nullable(),
  consumedUsd: z.number(),
  internalCostUsd: z.number(),
  budgetUsd: z.number().nullable(),
  percentUsed: z.number().nullable(),
  marginPercent: z.number(),
  expectedMarginPercent: z.number().nullable(),
  expiresAt: z.string().nullable(),
});

export const BillingPortfolioTotalsSchema = z.object({
  tenantCount: z.number(),
  budgetedTenantCount: z.number(),
  atRiskCount: z.number(),
  consumedUsd: z.number(),
  internalCostUsd: z.number(),
  budgetUsd: z.number().nullable(),
  percentUsed: z.number().nullable(),
  marginPercent: z.number(),
});

export const BillingPortfolioResponseSchema = z.object({
  totals: BillingPortfolioTotalsSchema,
  tenants: z.array(BillingPortfolioTenantSchema),
});

export const ModelBillingRateSchema = z.object({
  modelName: z.string(),
  internalInputPer1M: z.number().nullable(),
  internalOutputPer1M: z.number().nullable(),
  markupOverride: z.number().nullable(),
});

export const ListModelBillingRatesResponseSchema = z.object({
  rates: z.array(ModelBillingRateSchema),
});

export const UpsertModelBillingRateBodySchema = z.object({
  internalInputPer1M: z.number().positive().nullable(),
  internalOutputPer1M: z.number().positive().nullable(),
  markupOverride: z.number().positive().nullable(),
});

export const BillingNotificationConfigSchema = z.object({
  thresholds: z.array(z.number().int().positive().max(100)),
  customerEmails: z.array(z.string().email()),
  internalEmails: z.array(z.string().email()),
  watchAiCapacity: z.boolean(),
  watchApplications: z.boolean(),
  watchNetworkAssets: z.boolean(),
});

export const BillingBudgetKindSchema = z.enum([
  'AI_CAPACITY',
  'APPLICATIONS',
  'NETWORK_ASSETS',
]);

export const BillingNotificationStateItemSchema = z.object({
  budgetKind: BillingBudgetKindSchema,
  threshold: z.number().int(),
  notifiedAt: z.string(),
});

export const ListBillingNotificationStateResponseSchema = z.object({
  items: z.array(BillingNotificationStateItemSchema),
});

export const SendBillingTestAlertResponseSchema = z.object({
  recipientCount: z.number().int().nonnegative(),
});

export const BillingTierBudgetsSchema = z.object({
  specializedFrontierUsd: z.number().nonnegative().nullable(),
  heavyweightReasoningUsd: z.number().nonnegative().nullable(),
  productionUtilityUsd: z.number().nonnegative().nullable(),
});

export const BillingSettingsSchema = z.object({
  customerPriceMarkup: z.number().positive().nullable(),
  aiTierBudgets: BillingTierBudgetsSchema,
  applicationsBaselineBudget: z.number().int().nonnegative().nullable(),
  networkAssetsBaselineBudget: z.number().int().nonnegative().nullable(),
});

export const BillingGlobalSettingsSchema = z.object({
  defaultCustomerPriceMarkup: z.number().positive().nullable(),
});

export type BillingTier = z.infer<typeof BillingTierSchema>;
export type BillingAiCapacityDto = z.infer<typeof BillingAiCapacitySchema>;
export type BillingAiCapacityTrendDto = z.infer<
  typeof BillingAiCapacityTrendSchema
>;
export type BillingAiCapacityTrendGranularity = z.infer<
  typeof BillingAiCapacityTrendGranularitySchema
>;
export type BillingMarginDto = z.infer<typeof BillingMarginSchema>;
export type BillingMarginRowDto = z.infer<typeof BillingMarginRowSchema>;
export type BillingPortfolioTenantDto = z.infer<
  typeof BillingPortfolioTenantSchema
>;
export type BillingPortfolioDto = z.infer<
  typeof BillingPortfolioResponseSchema
>;
export type BillingBaselineDto = z.infer<typeof BillingBaselineSchema>;
export type BillingOverviewResponse = z.infer<
  typeof BillingOverviewResponseSchema
>;
export type ModelBillingRateDto = z.infer<typeof ModelBillingRateSchema>;
export type UpsertModelBillingRateBody = z.infer<
  typeof UpsertModelBillingRateBodySchema
>;
export type BillingNotificationConfigDto = z.infer<
  typeof BillingNotificationConfigSchema
>;
export type BillingCapacityForecastDto = z.infer<
  typeof BillingCapacityForecastSchema
>;
export type BillingNotificationStateItemDto = z.infer<
  typeof BillingNotificationStateItemSchema
>;
export type ListBillingNotificationStateResponseDto = z.infer<
  typeof ListBillingNotificationStateResponseSchema
>;
export type SendBillingTestAlertResponseDto = z.infer<
  typeof SendBillingTestAlertResponseSchema
>;
export type BillingSettingsDto = z.infer<typeof BillingSettingsSchema>;
export type BillingGlobalSettingsDto = z.infer<
  typeof BillingGlobalSettingsSchema
>;
